import { Link, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatTile } from '@/components/ui/stat-tile';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { formatMinor, useTurnover } from '@/hooks/use-shifts';
import { useT } from '@/i18n/use-t';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function dashboardWindow(params: URLSearchParams): { from: string; to: string } | undefined {
  const from = params.get('from');
  const to = params.get('to');
  if (from === null || to === null) return undefined;
  const duration = Date.parse(to) - Date.parse(from);
  if (!Number.isFinite(duration) || duration <= 0 || duration > 26 * 60 * 60 * 1000)
    return undefined;
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}

export function TurnoverPage() {
  const t = useT('admin_ui');
  /**
   * RL-377: the chosen day lives in the URL, so the dashboard's cash-variance
   * tile can deep-link to `/reports/turnover?date=…` and refresh, sharing and
   * browser Back all land on the same report. A missing or malformed `date`
   * falls back to today, which is what the page always did.
   */
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('date');
  const date = fromUrl !== null && ISO_DATE.test(fromUrl) ? fromUrl : todayIso();
  const window = dashboardWindow(params);
  const setDate = (next: string): void => {
    setParams(ISO_DATE.test(next) ? { date: next } : {});
  };
  const { data, isLoading } = useTurnover(date, window);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('reports.turnover.title', 'Turnover')}</h1>
        {/* The report is cash-only: rows come from the shift cash drawer, and a
            per-tender breakdown is not part of it. Say what the page shows, not
            what it lacks or when that might change (RL-331). */}
        <p className="text-sm text-muted-foreground">
          {t(
            'reports.turnover.intro',
            'Cash turnover for one day: every shift opened on the chosen date, with its opening float, the cash counted and expected at closing, and the difference.',
          )}
        </p>
      </header>

      <div className="flex items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="date">{t('reports.turnover.date', 'Date')}</Label>
          <Input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="max-w-[180px]"
          />
        </div>
      </div>

      {window !== undefined && (
        <p className="text-sm text-muted-foreground" data-testid="turnover-dashboard-window">
          {t('reports.turnover.dashboardWindow', 'Dashboard window: {from} to {to}')
            .replace('{from}', new Date(window.from).toLocaleString())
            .replace('{to}', new Date(window.to).toLocaleString())}{' '}
          <Link to={`/reports/turnover?date=${date}`} className="text-primary underline">
            {t('reports.turnover.clearWindow', 'Show UTC day instead')}
          </Link>
        </p>
      )}

      {/* ---------- Rollup ---------- */}

      {data !== undefined && (
        <section className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <StatTile
            label={t('shifts.list.title', 'Shifts')}
            value={String(data.rollup.shiftCount)}
          />
          <StatTile
            label={t('reports.turnover.openingSum', 'Opening (sum)')}
            value={formatMinor(data.rollup.openingCashMinor, data.rollup.currencyCode)}
          />
          <StatTile
            label={t('reports.turnover.closingCounted', 'Closing counted')}
            value={formatMinor(data.rollup.closingCashCountedMinor, data.rollup.currencyCode)}
          />
          <StatTile
            label={t('reports.turnover.closingExpected', 'Closing expected')}
            value={formatMinor(data.rollup.closingCashExpectedMinor, data.rollup.currencyCode)}
          />
          <StatTile
            label={t('reports.turnover.varianceSum', 'Variance (sum)')}
            value={formatMinor(data.rollup.varianceMinor, data.rollup.currencyCode)}
            highlight
          />
        </section>
      )}

      {/* ---------- Per-shift breakdown ---------- */}

      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('shifts.field.opened', 'Opened')}</TH>
              <TH>{t('shifts.field.cashier', 'Cashier')}</TH>
              <TH>{t('shifts.field.terminal', 'Terminal')}</TH>
              <TH>{t('shifts.field.state', 'State')}</TH>
              <TH className="text-right">{t('shifts.field.opening', 'Opening')}</TH>
              <TH className="text-right">{t('reports.turnover.counted', 'Counted')}</TH>
              <TH className="text-right">{t('reports.turnover.expected', 'Expected')}</TH>
              <TH className="text-right">{t('shifts.field.variance', 'Variance')}</TH>
              <TH className="text-right">{t('shifts.movements.kind.paidOut', 'Cash out')}</TH>
              <TH className="text-right">{t('shifts.movements.kind.paidIn', 'Cash in')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={11} className="py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {data?.shifts.length === 0 && (
              <TR>
                <TD colSpan={11} className="py-8 text-center text-muted-foreground">
                  {t('reports.turnover.empty', 'No shifts opened on {date}.').replace(
                    '{date}',
                    date,
                  )}
                </TD>
              </TR>
            )}
            {data?.shifts.map((row) => (
              <TR key={row.shiftId}>
                <TD className="text-xs">{formatTime(row.openedAt)}</TD>
                <TD className="text-sm">
                  <div className="font-medium">{row.cashier.displayName}</div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {row.cashier.employeeCode}
                  </div>
                </TD>
                <TD className="text-xs">
                  <div>{row.terminal.code}</div>
                  <div className="text-muted-foreground">{row.terminal.displayName}</div>
                </TD>
                <TD>
                  <Badge variant={row.state === 'reconciled' ? 'success' : 'outline'}>
                    {row.state}
                  </Badge>
                </TD>
                <TD className="text-right tabular-nums">
                  {formatMinor(row.openingCashMinor, row.currencyCode)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatMinor(row.closingCashCountedMinor, row.currencyCode)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatMinor(row.closingCashExpectedMinor, row.currencyCode)}
                </TD>
                <TD className="text-right tabular-nums">
                  <VarianceCell value={row.varianceMinor} currency={row.currencyCode} />
                </TD>
                <TD className="text-right tabular-nums text-muted-foreground">
                  {formatMinor(row.dropsMinor, row.currencyCode)}
                </TD>
                <TD className="text-right tabular-nums text-muted-foreground">
                  {formatMinor(row.pickupsMinor, row.currencyCode)}
                </TD>
                <TD className="text-right">
                  <Link
                    to={`/shifts/${row.shiftId}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('shifts.action.open', 'Open')}
                  </Link>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}

function VarianceCell({ value, currency }: { value: string | null; currency: string | null }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  const bi = BigInt(value);
  if (bi === 0n)
    return <span className="text-muted-foreground">{formatMinor(value, currency)}</span>;
  return (
    <span className={bi < 0n ? 'text-destructive' : 'text-primary'}>
      {formatMinor(value, currency)}
    </span>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
